import { Box, makeStyles, Menu, Theme } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { WbButton } from '../WbButton';
import { WbDivider } from '../WbDivider';
import clsx from 'clsx';

export interface WbCardActionsProps {
  children: ReactNode;
  breakpoint: Breakpoint;
  /**
   * applied on desktop container
   */
  className?: string;
}

type UseStylesProps = {
  breakpoint: Breakpoint;
};

/**
 * A responsive wrapper component that adapts its layout based on container size.
 *
 * On larger screens (above the specified breakpoint), children are displayed inline.
 * On smaller screens (below the breakpoint), children are collapsed into a menu accessible via a button.
 *
 * **Important:** For this component to work correctly, the parent container must have
 * `containerType: 'size'` or `containerType: 'inline-size'` set in its styles.
 * This enables CSS container queries which the component relies on for responsive behavior.
 *
 * @example
 * ```tsx
 * // Parent container must have containerType set
 * const useStyles = makeStyles({
 *   container: {
 *     width: '100%',
 *     containerType: 'inline-size',
 *   },
 * });
 *
 * // Usage
 * <Box className={classes.container}>
 *   <WbCardActions breakpoint="md">
 *     <WbButton label="Action 1" />
 *     <WbDivider />
 *     <WbButton label="Action 2" />
 *   </WbCardActions>
 * </Box>
 * ```
 *
 * @param children - The content to be displayed responsively
 * @param breakpoint - The breakpoint at which to switch between desktop and mobile layouts
 */
const useStyles = makeStyles<Theme, UseStylesProps>(theme => ({
  desktop: ({ breakpoint }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
      display: 'none',
    },
  }),
  mobile: ({ breakpoint }) => ({
    display: 'none',
    [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
      display: 'flex',
      marginLeft: 'auto',
    },
  }),
  mobileButton: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
  },
  menuContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    minWidth: '180px',
    containerType: 'inline-size',
  },
}));

export const WbCardActions: FC<WbCardActionsProps> = ({
  children,
  breakpoint,
  className,
}) => {
  const classes = useStyles({ breakpoint });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleResize = () => {
      handleClose();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  const transformChildrenForMobile = (childrenNodes: ReactNode): ReactNode => {
    return Children.map(childrenNodes, child => {
      if (isValidElement(child) && child.type === WbDivider) {
        return cloneElement(child, { orientation: 'horizontal' } as any);
      }
      return child;
    });
  };

  const mobileChildren = transformChildrenForMobile(children);

  return (
    <>
      <Box className={clsx(classes.desktop, className)}>{children}</Box>
      <Box className={classes.mobile}>
        <WbButton
          aria-label="open actions"
          aria-controls={open ? 'actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          icon={<MoreVertIcon />}
          color="default"
          variant="text"
          className={classes.mobileButton}
        />
        <Menu
          id="actions-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          getContentAnchorEl={null}
        >
          <Box className={classes.menuContent}>{mobileChildren}</Box>
        </Menu>
      </Box>
    </>
  );
};
